import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Text } from '@chilipiper/design-system'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { Button, EndContent, Input, InputGroup, Loader } from '@chilipiper/design-system/src/new'
import { IconArrowRight } from '@chilipiper/icons/figma-generated/src/icon'
import { endpoint } from '@chilipiper/config'
import { WelcomeForm, Progress, OnboardingChiliPiperLogo } from '@chilipiper/onboarding'
import {
  useSetUserSlug,
  useTenantSlugConfig,
  useUserSlug,
  useUserSlugConfig,
} from '@chilipiper/workspace-queries/src/api-queries/fire-router'
import { usePersonalDetails } from '@chilipiper/workspace-queries/src/api-queries/personal-details'
import { useFindPersonalDetailsById, useSetPersonalDetails } from '../../../api/personal-details'
import { spaceToDashAndLowercase } from '../../../utils/string'
import { routes } from '../../../routes/routes'
import {
  useAcceptInvite,
  useDeclineInvite,
  useOrganizationDetails,
  useTenant,
  useUser,
} from '../../../api/workspace'
import { useTenantDetails } from '../../../api/tenant'
import { Spacer } from '../components/Spacer'
import { useOnboardingExtraSteps } from '../steps'
import { shouldShowRequestLicenses } from './helpers'
import { useTrackFeature } from '@/hooks/useTracking'
import { ChilicalEvent, ChilicalFeature } from '@/tracking/events'

export const Welcome = () => {
  const { userId } = useFireSessionContext()
  const { data: tenantData } = useOrganizationDetails()
  const { data: userData } = useUser({ suspense: true })
  const { data: userSlug } = useUserSlug({ suspense: true, userId })
  const { data, isLoading: isPersonalDetailsLoading, isError } = useFindPersonalDetailsById(userId)
  const { isLoading: isTenantSlugConfigLoading } = useTenantSlugConfig()
  const { isLoading: isUserSlugConfigLoading } = useUserSlugConfig()
  const { mutate: acceptInvite, isPending: isAccepting } = useAcceptInvite()
  const { mutate: declineInvite, isPending: isDeclining } = useDeclineInvite()
  const track = useTrackFeature()
  const extraSteps = useOnboardingExtraSteps()

  if (userData?.invitedAt) {
    const orgName = tenantData?.name ?? 'an organization'
    return (
      <Flex
        alignItems='center'
        w='100%'
        h='100%'
        flexDirection='column'
        bg='bg/minor'
        overflow='auto'
        px={6}
        py={5}
      >
        <Spacer />
        <OnboardingChiliPiperLogo />
        <Flex flexDirection='column' gap={4} maxWidth='500px'>
          <Text color='fg/major' fontSize={24} lineHeight={8} fontWeight={600} textAlign='center'>
            You have been invited to join {orgName}.
          </Text>
          <Text textStyle='body-multiline' color='fg/major' textAlign='center'>
            You can accept or decline the invitation.
          </Text>
          <Button
            variant='major'
            isFullWidth
            isLoading={isAccepting}
            onClick={() => {
              acceptInvite(undefined, {
                onSuccess: () =>
                  track(ChilicalEvent.OnboardingInviteAccepted, ChilicalFeature.Onboarding),
              })
            }}
          >
            Accept invitation
          </Button>
          <Button
            variant='minor'
            isFullWidth
            isLoading={isDeclining}
            onClick={() => {
              declineInvite(undefined, {
                onSuccess: () =>
                  track(ChilicalEvent.OnboardingInviteDeclined, ChilicalFeature.Onboarding),
              })
            }}
          >
            Decline Invitation
          </Button>
        </Flex>
        <Spacer />
      </Flex>
    )
  }

  const isLoading = isPersonalDetailsLoading || isTenantSlugConfigLoading || isUserSlugConfigLoading

  if (isLoading) {
    return (
      <WelcomeForm
        heading={TITLE}
        main={
          <Flex alignItems='center' justifyContent='center' w='100%' h='238px'>
            <Loader />
          </Flex>
        }
        footer={
          <Box w='100%'>
            <Button
              variant='major'
              isDisabled
              type='submit'
              endContent={<Button.EndContent variant='icon' icon={IconArrowRight} />}
              mb={9}
              ml='auto'
            >
              Next
            </Button>
            <Progress totalSteps={4 + extraSteps} currentStep={2} />
          </Box>
        }
      />
    )
  }

  if (isError) {
    return null
  }

  return (
    <InteractiveWelcomeForm
      initialFirstName={data?.firstName ?? ''}
      initialLastName={data?.lastName ?? ''}
      initialJobTitle={data?.jobTitle ?? ''}
      initialUserSlug={userSlug}
      email={data?.email ?? ''}
    />
  )
}

const createSlug = (firstName: string, lastName: string) => {
  return `${spaceToDashAndLowercase(firstName)}${lastName && '-'}${spaceToDashAndLowercase(
    lastName
  )}`
}

const InteractiveWelcomeForm = (props: {
  email: string
  initialFirstName: string
  initialLastName: string
  initialUserSlug?: string | null
  initialJobTitle: string
}) => {
  const navigate = useNavigate()
  const track = useTrackFeature()
  const extraSteps = useOnboardingExtraSteps()
  const { mutateAsync: mutatePersonalDetails, isPending: isSettingPersonalDetails } =
    useSetPersonalDetails()
  const { data: userData } = useUser()
  const { data: personalDetails, isLoading: isPersonalDetailsLoading } = usePersonalDetails(
    userData?.id
  )
  const { data: tenantSlugConfig } = useTenantSlugConfig()
  const { data: userSlugConfig } = useUserSlugConfig()
  const { data: tenantData } = useTenant()
  const { data: tenantDetails } = useTenantDetails()
  const { mutateAsync: mutateSlug, isPending: isSettingSlug } = useSetUserSlug()
  const [slugError, setSlugError] = useState<string | undefined>()
  const [firstName, setFirstName] = useState(props.initialFirstName)
  const [lastName, setLastName] = useState(props.initialLastName)
  const [jobTitle, setJobTitle] = useState(props.initialJobTitle)
  const [customSlug, setCustomSlug] = useState<string | undefined>(undefined)
  const defaultSlug = createSlug(firstName, lastName)
  const slug = customSlug ?? props.initialUserSlug ?? defaultSlug
  const [previousSlug, setPreviousSlug] = useState(slug)

  if (slug !== previousSlug) {
    setSlugError(undefined)
    setPreviousSlug(slug)
  }

  const isSubmitDisabled = !firstName || !lastName || !jobTitle || !slug || isPersonalDetailsLoading

  const canEditSlug = Boolean(
    tenantSlugConfig?.slugsUserEditable || userSlugConfig?.slugsUserEditable
  )

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      return
    }

    Promise.all([
      mutateSlug({
        userSlug: slug,
      }).catch(async e => {
        if (e.status === 409) {
          if (!canEditSlug) {
            await autoincrementUserSlug(slug, mutateSlug)
          }

          setSlugError('This URL is already taken. Please choose another.')
          return
        }

        throw e
      }),
      mutatePersonalDetails({
        ...(personalDetails ?? {}),
        firstName,
        lastName,
        email: props.email,
        jobTitle,
      }),
    ]).then(() => {
      track(ChilicalEvent.OnboardingProfileCompleted, ChilicalFeature.Onboarding, {
        job_title: jobTitle,
      })
      const isOrgTenant = tenantDetails?.tenantInfo.type === 'Organization'
      const hasNoLicenses = Object.values(userData?.licenses ?? {}).filter(Boolean).length === 0
      if (
        isOrgTenant &&
        hasNoLicenses &&
        tenantData?.claimStatus === 'Claimed' &&
        shouldShowRequestLicenses()
      ) {
        navigate(routes.onboarding.requestLicenses)
      } else {
        navigate(routes.onboarding.workingHours)
      }
    })
  }
  const title = firstName ? `Welcome to ChiliCal, ${firstName} 👋` : TITLE

  return (
    <WelcomeForm
      handleSubmit={handleSubmit}
      heading={title}
      main={
        <>
          <Flex gap={5} flexDirection={['column', 'column', 'row']}>
            <Input
              data-test-id='onboarding-firstname'
              label={
                <Input.Label variant='moderate' position='top'>
                  First Name
                </Input.Label>
              }
              onChange={value => setFirstName(value)}
              placeholder=''
              value={firstName}
            />
            <Input
              data-test-id='onboarding-lastname'
              label={
                <Input.Label variant='moderate' position='top'>
                  Last Name
                </Input.Label>
              }
              onChange={value => setLastName(value)}
              placeholder=''
              value={lastName}
            />
          </Flex>
          <Input
            data-test-id='onboarding-jobtitle'
            label={
              <Input.Label variant='moderate' position='top'>
                Job Title
              </Input.Label>
            }
            onChange={value => setJobTitle(value)}
            placeholder=''
            value={jobTitle}
          />
          {canEditSlug ? (
            <InputGroup
              label={
                <InputGroup.Label
                  variant='moderate'
                  position='top'
                  endContent={
                    canEditSlug ? undefined : (
                      <EndContent
                        variant='iconTooltip'
                        content='Editing your URL is disabled by your administrator'
                      />
                    )
                  }
                >
                  Your unique Chili Piper URL
                </InputGroup.Label>
              }
            >
              <InputGroup.Row gridAutoColumns='auto 1fr'>
                <InputGroup.Text>{`${endpoint.fire()}/me/`}</InputGroup.Text>
                <Input
                  isDisabled={!canEditSlug}
                  variant='nested'
                  data-test-id='onboarding-slug'
                  label={<Input.Label position='hidden'>custom path segment</Input.Label>}
                  value={slug}
                  onChange={value => setCustomSlug(value)}
                  placeholder=''
                  errorMessage={slugError}
                />
              </InputGroup.Row>
            </InputGroup>
          ) : null}
        </>
      }
      footer={
        <Box w='100%'>
          <Button
            variant='major'
            isLoading={isSettingPersonalDetails || isSettingSlug}
            isDisabled={isSubmitDisabled}
            type='submit'
            data-test-id='onboarding-next'
            endContent={<Button.EndContent variant='icon' icon={IconArrowRight} />}
            mb={9}
            ml='auto'
          >
            Next
          </Button>
          <Progress totalSteps={5 + extraSteps} currentStep={2} />
        </Box>
      }
    />
  )
}

const TITLE = 'Welcome to ChiliCal 👋'

const incrementSlug = (slug: string) => {
  const postfix = parseInt(slug.slice(slug.length - 2), 10)

  if (isNaN(postfix)) {
    return `${slug}-1`
  }

  return `${slug.slice(0, slug.length - 2)}${postfix - 1}`
}

const autoincrementUserSlug = async (
  slug: string,
  setSlug: (variables: { userSlug: string }) => Promise<unknown>
) => {
  const newSlug = incrementSlug(slug)
  try {
    await setSlug({ userSlug: newSlug })
  } catch (e: unknown) {
    if ((e as any).status === 409) {
      return await autoincrementUserSlug(newSlug, setSlug)
    }

    throw e
  }
}
